import { StyleSheet, css } from 'aphrodite'
import { useEffect, useMemo, useState } from 'react'
import { userService } from '../../services/userService'
import { StakeholderType, UserType } from '../../types/user'
import type { Contact, User } from '../../types/user'
import type { WorkOrder } from '../../types/workorder'
import MultiSelect, { type Option } from '../MultiSelect'

interface ContactInfoStepProps {
  data: Partial<WorkOrder>
  onUpdate: (data: Partial<WorkOrder>) => void
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
}

interface DispatcherOption {
  id: number
  name: string
  email: string
  isTeamMember: boolean
  userId?: number // Present for team members, optional for contacts
}

interface DriverOption {
  id: number
  name: string
  email: string
  isTeamMember: boolean
  userId?: number // Present for team members, optional for contacts
}

const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  data,
  onUpdate,
  onValidationChange,
  showErrors = false,
}) => {
  const [subhaulers, setSubhaulers] = useState<Contact[]>([])
  const [dispatchers, setDispatchers] = useState<DispatcherOption[]>([])
  const [drivers, setDrivers] = useState<DriverOption[]>([])

  // Validation: Check if at least one subhauler and one dispatcher are selected
  useEffect(() => {
    const hasSubhaulers = Boolean(data.subhaulers && data.subhaulers.length > 0)
    const hasDispatchers = Boolean(
      data.dispatchers && data.dispatchers.length > 0,
    )
    const isValid = hasSubhaulers && hasDispatchers

    console.log(
      'WorkOrder ContactInfoStep validation - subhaulers:',
      data.subhaulers,
      'dispatchers:',
      data.dispatchers,
      'isValid:',
      isValid,
    )
    if (onValidationChange) {
      onValidationChange(isValid)
    }
  }, [data.subhaulers, data.dispatchers, onValidationChange])

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const [contacts, teamMembers] = await Promise.all([
          userService.getContacts(),
          userService.getTeamMembers(),
        ])

        // Get subhauler contacts - filter to only show owners
        setSubhaulers(
          contacts.filter(
            (c) =>
              c.contact_object.stakeholder_types?.includes(
                StakeholderType.SUB_HAULER,
              ) && c.contact_object.user_type === UserType.OWNER,
          ),
        )

        // Get dispatcher contacts and convert to DispatcherOption
        const dispatcherContacts: DispatcherOption[] = contacts
          .filter((c) => c.contact_object.user_type === UserType.DISPATCHER)
          .map((contact) => ({
            id: contact.id,
            name: contact.contact_object.name || contact.contact_object.email,
            email: contact.contact_object.email,
            isTeamMember: false,
            userId: contact.contact_object.user_id,
          }))

        // Get team members with DISPATCHER user type and convert to DispatcherOption
        const dispatcherTeamMembers: DispatcherOption[] = teamMembers
          .filter((user: User) => user.user_type === UserType.DISPATCHER)
          .map((user: User) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`.trim() || user.email,
            email: user.email,
            isTeamMember: true,
            userId: user.id,
          }))

        // Combine and deduplicate by user ID (team members take precedence)
        const allDispatchers = [...dispatcherContacts, ...dispatcherTeamMembers]
        const uniqueDispatchers = allDispatchers.filter(
          (dispatcher, index, self) => {
            if (dispatcher.isTeamMember) {
              // For team members, deduplicate by userId
              return (
                index ===
                self.findIndex(
                  (d) => d.isTeamMember && d.userId === dispatcher.userId,
                )
              )
            } else {
              // For contacts, deduplicate by userId if available, otherwise by contact id
              if (dispatcher.userId) {
                return (
                  index ===
                  self.findIndex((d) => d.userId === dispatcher.userId)
                )
              } else {
                return (
                  index ===
                  self.findIndex(
                    (d) => !d.isTeamMember && d.id === dispatcher.id,
                  )
                )
              }
            }
          },
        )

        setDispatchers(uniqueDispatchers)

        // Get driver contacts and convert to DriverOption
        const driverContacts: DriverOption[] = contacts
          .filter((c) => c.contact_object.user_type === UserType.DRIVER)
          .map((contact) => ({
            id: contact.id,
            name: contact.contact_object.name || contact.contact_object.email,
            email: contact.contact_object.email,
            isTeamMember: false,
            userId: contact.contact_object.user_id,
          }))

        // Get team members with DRIVER user type and convert to DriverOption
        const driverTeamMembers: DriverOption[] = teamMembers
          .filter((user: User) => user.user_type === UserType.DRIVER)
          .map((user: User) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`.trim() || user.email,
            email: user.email,
            isTeamMember: true,
            userId: user.id,
          }))

        // Combine and deduplicate drivers by user ID (team members take precedence)
        const allDrivers = [...driverContacts, ...driverTeamMembers]
        const uniqueDrivers = allDrivers.filter((driver, index, self) => {
          if (driver.isTeamMember) {
            // For team members, deduplicate by userId
            return (
              index ===
              self.findIndex(
                (d) => d.isTeamMember && d.userId === driver.userId,
              )
            )
          } else {
            // For contacts, deduplicate by userId if available, otherwise by contact id
            if (driver.userId) {
              return index === self.findIndex((d) => d.userId === driver.userId)
            } else {
              return (
                index ===
                self.findIndex((d) => !d.isTeamMember && d.id === driver.id)
              )
            }
          }
        })

        setDrivers(uniqueDrivers)
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      }
    }

    fetchContacts()
  }, [])

  // Convert selected subhaulers to options for display
  const subhaulerOptions = useMemo(() => {
    if (!data.subhaulers || data.subhaulers.length === 0) return []

    return data.subhaulers.map((subhauler) => ({
      label:
        `${subhauler.first_name} ${subhauler.last_name}`.trim() ||
        subhauler.email,
      value: subhauler.id.toString(),
      name:
        `${subhauler.first_name} ${subhauler.last_name}`.trim() ||
        subhauler.email,
      email: subhauler.email,
    }))
  }, [data.subhaulers])

  // Convert selected dispatchers to options for display
  const dispatcherOptions = useMemo(() => {
    if (!data.dispatchers || data.dispatchers.length === 0) return []

    return data.dispatchers.map((dispatcher) => ({
      label:
        `${dispatcher.first_name} ${dispatcher.last_name}`.trim() ||
        dispatcher.email,
      value: dispatcher.id.toString(),
      name:
        `${dispatcher.first_name} ${dispatcher.last_name}`.trim() ||
        dispatcher.email,
      email: dispatcher.email,
    }))
  }, [data.dispatchers])

  // Convert selected drivers to options for display
  const driverOptions = useMemo(() => {
    if (!data.drivers || data.drivers.length === 0) return []

    return data.drivers.map((driver) => ({
      label: `${driver.first_name} ${driver.last_name}`.trim() || driver.email,
      value: driver.id.toString(),
      name: `${driver.first_name} ${driver.last_name}`.trim() || driver.email,
      email: driver.email,
    }))
  }, [data.drivers])

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>CONTACT INFORMATION</h3>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Sub Hauler *</label>
        <MultiSelect
          isMulti={true}
          placeholder="Select Sub Hauler"
          options={subhaulers.map((subhauler) => ({
            label: `${subhauler.contact_object.name} (${subhauler.contact_object.email})`,
            value: subhauler.contact_object.id.toString(), // Use User ID to match subhaulerOptions
            name: subhauler.contact_object.name,
            email: subhauler.contact_object.email,
          }))}
          value={subhaulerOptions}
          showEmailInOption={true}
          onChange={(selected: Option | Option[] | null) => {
            const selectedOptions = Array.isArray(selected)
              ? selected
              : selected
                ? [selected]
                : []

            const selectedSubhaulers = selectedOptions
              .map((option) => {
                const subhauler = subhaulers.find(
                  (s) => s.contact_object.id.toString() === option.value,
                )

                if (subhauler) {
                  return {
                    id: subhauler.contact_object.id,
                    first_name:
                      subhauler.contact_object.name?.split(' ')[0] || '',
                    last_name:
                      subhauler.contact_object.name
                        ?.split(' ')
                        .slice(1)
                        .join(' ') || '',
                    email: subhauler.contact_object.email,
                    user_type: UserType.OWNER,
                    stakeholder_types: [StakeholderType.SUB_HAULER],
                    username: subhauler.contact_object.email,
                    phone_number: '',
                    has_completed_driver_onboarding: false,
                    has_completed_contractor_onboarding: false,
                    current_profile: {
                      id: subhauler.contact_object.id,
                      stakeholder_type: StakeholderType.SUB_HAULER,
                    },
                  } as User
                }
                return null
              })
              .filter(Boolean) as User[]

            onUpdate({
              subhaulers: selectedSubhaulers,
            })
          }}
        />
        {showErrors && (!data.subhaulers || data.subhaulers.length === 0) && (
          <div className={css(styles.errorMessage)}>
            Please select at least one sub hauler to continue.
          </div>
        )}
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Dispatcher *</label>
        <MultiSelect
          isMulti={true}
          placeholder="Select Dispatcher"
          options={useMemo(
            () =>
              dispatchers.map((dispatcher) => ({
                label: `${dispatcher.name} (${dispatcher.email})${dispatcher.isTeamMember ? ' - Team Member' : ''}`,
                value: dispatcher.id.toString(),
                name: dispatcher.name,
                email: dispatcher.email,
              })),
            [dispatchers],
          )}
          value={dispatcherOptions}
          showEmailInOption={true}
          onChange={(selected: Option | Option[] | null) => {
            const selectedOptions = Array.isArray(selected)
              ? selected
              : selected
                ? [selected]
                : []
            const selectedDispatchers = selectedOptions
              .map((option) => {
                const dispatcher = dispatchers.find(
                  (d) => d.id.toString() === option.value,
                )
                if (dispatcher) {
                  return {
                    id: dispatcher.userId || dispatcher.id,
                    first_name: dispatcher.name.split(' ')[0] || '',
                    last_name:
                      dispatcher.name.split(' ').slice(1).join(' ') || '',
                    email: dispatcher.email,
                    user_type: UserType.DISPATCHER,
                    stakeholder_types: [StakeholderType.SUB_HAULER],
                    username: dispatcher.email,
                    phone_number: '',
                    has_completed_driver_onboarding: false,
                    has_completed_contractor_onboarding: false,
                    current_profile: {
                      id: dispatcher.userId || dispatcher.id,
                      stakeholder_type: StakeholderType.SUB_HAULER,
                    },
                  } as User
                }
                return null
              })
              .filter(Boolean) as User[]

            onUpdate({
              dispatchers: selectedDispatchers,
            })
          }}
        />
        {showErrors && (!data.dispatchers || data.dispatchers.length === 0) && (
          <div className={css(styles.errorMessage)}>
            Please select at least one dispatcher to continue.
          </div>
        )}
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Driver (optional)</label>
        <MultiSelect
          isMulti={true}
          placeholder="Select Driver"
          options={useMemo(
            () =>
              drivers.map((driver) => ({
                label: `${driver.name} (${driver.email})${driver.isTeamMember ? ' - Team Member' : ''}`,
                value: driver.id.toString(),
                name: driver.name,
                email: driver.email,
              })),
            [drivers],
          )}
          value={driverOptions}
          showEmailInOption={true}
          onChange={(selected: Option | Option[] | null) => {
            const selectedOptions = Array.isArray(selected)
              ? selected
              : selected
                ? [selected]
                : []
            const selectedDrivers = selectedOptions
              .map((option) => {
                const driver = drivers.find(
                  (d) => d.id.toString() === option.value,
                )
                if (driver) {
                  return {
                    id: driver.userId || driver.id,
                    first_name: driver.name.split(' ')[0] || '',
                    last_name: driver.name.split(' ').slice(1).join(' ') || '',
                    email: driver.email,
                    user_type: UserType.DRIVER,
                    stakeholder_types: [StakeholderType.SUB_HAULER],
                    username: driver.email,
                    phone_number: '',
                    has_completed_driver_onboarding: false,
                    has_completed_contractor_onboarding: false,
                    current_profile: {
                      id: driver.userId || driver.id,
                      stakeholder_type: StakeholderType.SUB_HAULER,
                    },
                  } as User
                }
                return null
              })
              .filter(Boolean) as User[]

            onUpdate({
              drivers: selectedDrivers,
            })
          }}
        />
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#374151',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
  },
})

export default ContactInfoStep
